import React, { useEffect, useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import {
  candidatsIdentite,
  champsDivergents,
  patchIdentiteRetenue,
  tableauDesSources,
} from "../../Utils/choixIdentite";

/**
 * Nicolas 13/09 : « j'ai lu tout ça sur le Data Matrix, tout ça sur la CNI…
 * qu'est-ce que je dois garder ? »
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LE 18/09 : EN TABLEAU, ET LA VALEUR GARDÉE EN VERT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * « Le système de discordance d'identité est mal fait. J'imagine un tableau
 * avec en en-tête le nom des sources et dans les différentes lignes ce qui a
 * été lu avec les éventuelles discordances et en effet quelle valeur
 * conservée. […] Fais nous sélectionner la bonne valeur et passe là en vert
 * ou avec un liseré vert qu'on ait bien la visu de ce qu'on a choisi. »
 *
 * Trois conséquences dans ce fichier :
 *   1. une colonne par source, une ligne par champ — la colonne qui diverge
 *      se voit sans lire une seule étiquette ;
 *   2. la case retenue porte un liseré vert, et elle seule ;
 *   3. le choix remonte la PROVENANCE avec la valeur (voir
 *      `patchIdentiteRetenue`) : une valeur venue du Data Matrix ne doit plus
 *      réapparaître estampillée « Manuel ».
 *
 * Invisible s'il n'y a qu'une source par champ.
 */
export default function ChoixIdentite({ appointment, appointmentId, firebase, onTravail, onApplique }) {
  const candidats = useMemo(() => candidatsIdentite(appointment || {}), [appointment]);
  const divergents = champsDivergents(candidats);
  const { sources, lignes } = useMemo(
    () => tableauDesSources(divergents),
    [divergents]
  );

  const [choix, setChoix] = useState({});
  const [ouvert, setOuvert] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState(null);

  /*
    Le choix suit le dossier ouvert.

    Il était posé par l'initialiseur de `useState`, qui ne s'exécute qu'au
    PREMIER montage : en passant d'un dossier à l'autre, le composant gardait
    les valeurs cochées du dossier précédent. On les recalcule donc à chaque
    changement de rendez-vous.
  */
  useEffect(() => {
    setChoix(
      Object.fromEntries(
        candidats.map((c) => [c.id, c.retenu || c.options[0]?.valeur || ""])
      )
    );
    setErreur(null);
  }, [appointmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!divergents.length) return null;

  async function garder() {
    const selection = candidats.map((c) => ({ ...c, retenu: choix[c.id] || c.retenu }));
    const patch = patchIdentiteRetenue(selection, appointment || {});
    if (!appointmentId || !firebase?.db) return;
    setEnregistrement(true);
    setErreur(null);
    try {
      await updateDoc(doc(firebase.db, "appointements", appointmentId), patch);
      onApplique?.(patch);
      onTravail?.();
    } catch (e) {
      setErreur(e.message || "Enregistrement refusé");
    } finally {
      setEnregistrement(false);
    }
  }

  return (
    <div className="sb-card">
      <h3>
        <span className="sb-icon">&#x2696;</span> Discordances
        <button type="button" className="sb-btnMini2" style={{ marginLeft: 8 }} onClick={() => setOuvert((o) => !o)}>
          {ouvert ? "Replier" : `${divergents.length} écart(s)`}
        </button>
      </h3>
      {ouvert ? (
        <>
          <div className="sb-pill sb-nv" style={{ marginBottom: 8 }}>
            Les documents ne disent pas la même chose. Touchez la valeur à garder.
          </div>

          <div className="sb-discordTable">
            <table>
              <thead>
                <tr>
                  <th className="sb-discordChamp">Champ</th>
                  {sources.map((s) => (
                    <th key={s}>{s}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => (
                  <tr key={ligne.id}>
                    <th scope="row" className="sb-discordChamp">{ligne.label}</th>
                    {sources.map((s) => {
                      const valeur = ligne.valeurs[s];
                      if (!valeur) {
                        return (
                          <td key={s} className="sb-discordVide">—</td>
                        );
                      }
                      const retenue = choix[ligne.id] === valeur;
                      return (
                        <td key={s}>
                          <button
                            type="button"
                            className={`sb-discordCase${retenue ? " sb-discordRetenue" : ""}`}
                            onClick={() => setChoix({ ...choix, [ligne.id]: valeur })}
                            aria-pressed={retenue}
                          >
                            <span className="sb-discordValeur">
                              {valeur}
                              {/* Les sources qui disent la MEME chose sont
                                  nommées ici, en petit, au lieu d'occuper une
                                  colonne où la valeur serait répétée
                                  (Nicolas, 18/09 18h18). */}
                              {ligne.confirmations?.[s]?.length ? (
                                <span className="sb-discordAussi">
                                  aussi {ligne.confirmations[s].join(", ").toLowerCase()}
                                </span>
                              ) : null}
                            </span>
                            {retenue && <span className="sb-discordCoche" aria-hidden="true">✓</span>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {erreur ? <div className="sb-pill sb-nv">{erreur}</div> : null}
          <button className="sb-btnMini sb-ok" type="button" onClick={garder} disabled={enregistrement}>
            {enregistrement ? "Enregistrement…" : "Garder ces valeurs"}
          </button>
        </>
      ) : null}
    </div>
  );
}
